<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue"; // ref is a function that creates a reactive reference to a value
import { useUserStore } from "../stores/user.js";
import { useRouter, useRoute } from "vue-router";
import { supabase } from "../lib/supabase.js";
const userStore = useUserStore();
const email = ref(""); // Dynamically updated by form
const password = ref(""); // Dynamically updated by form
const name = ref(""); // Dynamically updated by form
const error = ref("");
const isLoading = ref(false);
const router = useRouter();
const route = useRoute();
const loggingIn = ref(!(route?.query?.mode === "signup"));
const emailSent = ref(false); // Flag to indicate if the email has been sent
const resetLoading = ref(false);
const resetMessage = ref("");
const resetError = ref("");

// Force Login page to always use light theme while mounted
let previousTheme = null;
let previousBodyBg = "";
let previousThemeColor = "";
let previousHtmlColorScheme = "";
let previousMetaColorScheme = "";

onMounted(() => {
  const root = document.documentElement;
  previousTheme = root.getAttribute("data-theme");
  previousBodyBg = document.body.style.backgroundColor || "";

  const meta =
    document.querySelector('meta#dynamic-theme-color[name="theme-color"]') ||
    document.querySelector('meta[name="theme-color"]');
  previousThemeColor = meta?.getAttribute("content") || "";

  // Capture and force page-level color-scheme to light
  const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  previousHtmlColorScheme = root.style.colorScheme || "";
  previousMetaColorScheme = colorSchemeMeta?.getAttribute("content") || "";
  root.style.colorScheme = "light";
  if (colorSchemeMeta) colorSchemeMeta.setAttribute("content", "light");

  // Force light theme visuals
  root.setAttribute("data-theme", "light");
  document.body.style.backgroundColor = "#ffffff";
  if (meta) meta.setAttribute("content", "#ffffff");
});

onBeforeUnmount(() => {
  const root = document.documentElement;
  if (previousTheme) root.setAttribute("data-theme", previousTheme);
  else root.removeAttribute("data-theme");

  document.body.style.backgroundColor = previousBodyBg;

  const meta =
    document.querySelector('meta#dynamic-theme-color[name="theme-color"]') ||
    document.querySelector('meta[name="theme-color"]');
  if (meta && previousThemeColor)
    meta.setAttribute("content", previousThemeColor);

  // Restore page-level color-scheme and meta
  const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  if (previousHtmlColorScheme) root.style.colorScheme = previousHtmlColorScheme;
  else root.style.colorScheme = "";
  if (colorSchemeMeta && previousMetaColorScheme)
    colorSchemeMeta.setAttribute("content", previousMetaColorScheme);
});

const sendPasswordReset = async () => {
  resetError.value = "";
  resetMessage.value = "";
  if (!email.value) {
    resetError.value = "Please enter your email above.";
    return;
  }
  resetLoading.value = true;
  try {
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(
      email.value,
      { redirectTo: `${window.location.origin}/auth/callback` }
    );
    if (resetErr) {
      resetError.value = resetErr.message;
    } else {
      resetMessage.value =
        "If an account exists for that email, a reset link has been sent.";
    }
  } catch (e) {
    resetError.value = e?.message || "Unexpected error sending reset email.";
  } finally {
    resetLoading.value = false;
  }
};

const handleSubmission = async () => {
  console.log("handleSubmission loggingIn.value: ", loggingIn.value);
  if (loggingIn.value) {
    // The user is logging in
    const login_object = await userStore.login(email.value, password.value);
    if (login_object.error) {
      // if .error doesn't exist, then it will evaluate to false
      error.value = login_object.error;
    } else {
      // Redirect to the home page or dashboard
      router.push("/");
    }
  } else {
    // The user is signing up
    const signup_object = await userStore.signup(
      email.value,
      password.value,
      name.value
    );
    if (signup_object.error) {
      error.value = signup_object.error;
    } else {
      // The user signed up successfully
      emailSent.value = true; // Set the flag to true to show the email sent message
    }
  }
};
</script>

<template>
  <div class="login-container">
    <form
      v-if="!emailSent"
      @submit.prevent="handleSubmission"
      class="login-form"
    >
      <h2 v-if="loggingIn">Login</h2>
      <h2 v-else>Sign Up</h2>

      <!-- Add name field only for signup -->
      <div v-if="!loggingIn" class="form-group">
        <label>First name</label>
        <input
          type="text"
          v-model="name"
          required
          placeholder="Enter your first name"
        />
      </div>

      <div class="form-group">
        <label>Email</label>
        <input
          type="email"
          v-model="email"
          required
          placeholder="Enter your email"
        />
      </div>

      <div class="form-group">
        <label>Password</label>
        <input
          type="password"
          v-model="password"
          required
          placeholder="Enter your password"
        />
      </div>

      <button v-if="loggingIn" type="submit" :disabled="isLoading">
        {{ isLoading ? "Logging in..." : "Login" }}
      </button>

      <button v-else type="submit" :disabled="isLoading">
        {{ isLoading ? "Signing up..." : "Sign Up" }}
      </button>

      <p v-if="error" class="error-message">{{ error }}</p>

      <!-- Changing the login form to a submission form if they click on "Sign Up". Not taking them to a new page -->
      <p v-if="loggingIn" class="signup-link">
        Don't have an account?
        <a href="#" @click.prevent="loggingIn = false">Sign Up</a>
      </p>
      <p v-else class="signup-link">
        Already have an account?
        <a href="#" @click.prevent="loggingIn = true">Login</a>
      </p>
      <p v-if="loggingIn" class="forgot-password">
        <a
          href="#"
          @click.prevent="sendPasswordReset"
          :aria-busy="resetLoading"
        >
          {{ resetLoading ? "Sending reset email..." : "Forgot Password?" }}
        </a>
      </p>
      <p v-if="resetMessage" class="info-message">{{ resetMessage }}</p>
      <p v-if="resetError" class="error-message">{{ resetError }}</p>
    </form>
    <div v-else class="email-sent-message">
      <h2>Account Confirmation Email Sent</h2>
      <p>Please check your email for the verification link.</p>
      <button @click="emailSent = false">Back to Login</button>
    </div>
  </div>
</template>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f5f5f5;
}

.login-form {
  color-scheme: light;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  margin-right: 15px;
  margin-left: 15px;
}

.form-group {
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  width: 100%;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  align-self: flex-start;
}

input {
  width: 100%;
  box-sizing: border-box;
  display: block;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: #ffffff !important;
  color: #111827 !important;
  caret-color: #111827;
  outline-color: #99b5d3;
  color-scheme: light !important;
  -webkit-appearance: none;
  appearance: none;
}

/* Ensure placeholders are readable in forced-light mode */
input::placeholder {
  color: #6b7280; /* gray-500 */
}

/* Force autofill to render as light */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: #111827 !important;
  -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  box-shadow: 0 0 0px 1000px #ffffff inset !important;
}

input:focus {
  border-color: #99b5d3 !important;
  box-shadow: 0 0 0 2px rgba(153, 181, 211, 0.35) !important;
  outline: none;
}

button {
  width: 100%;
  box-sizing: border-box;
  padding: 1rem;
  background-color: #99b5d3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
}

button:hover {
  background-color: #7d9cbb;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #ff4444;
  margin: 1rem 0;
  text-align: center;
}

.signup-link {
  display: block;
  text-align: center;
  margin-top: 1rem;
  color: #99b5d3;
  text-decoration: none;
}

.signup-link:hover {
  text-decoration: underline;
}

.forgot-password {
  display: block;
  text-align: center;
  margin-top: 0.5rem;
  color: #99b5d3;
}

.forgot-password a {
  color: inherit;
  text-decoration: none;
}

.forgot-password a:hover {
  text-decoration: underline;
}

.info-message {
  color: #2e7d32;
  margin: 0.5rem 0;
  text-align: center;
  font-size: 0.95rem;
}

.email-sent-message {
  margin-left: 15px;
  margin-right: 15px;
}

.email-sent-message a {
  color: #99b5d3;
  text-decoration: none;
}

.email-sent-message a:hover {
  text-decoration: underline;
}
</style>
